import React, { ReactNode } from 'react';
import styled from 'styled-components';
import { Header } from './Header';
import { Sidebar } from './Sidebar';
import { SidebarProvider, useSidebar } from '../../contexts/SidebarContext';
import { FiMenu } from '../../utils/icons';

const LayoutContainer = styled.div`
  display: flex;
  min-height: 100vh;
  background-color: ${({ theme }) => theme.colors.background};
`;

const MainContent = styled.main<{ $isCollapsed: boolean }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  margin-left: ${({ $isCollapsed }) => ($isCollapsed ? '0' : '200px')};
  transition: margin-left ${({ theme }) => theme.transitions.normal};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    margin-left: 0;
  }
`;

const ContentArea = styled.div`
  flex: 1;
  padding: 0;
  width: 100%;
  overflow: hidden;
`;

const FloatingMenuButton = styled.button<{ $isVisible: boolean }>`
  position: fixed;
  top: 95px;
  left: 12px;
  width: 40px;
  height: 40px;
  background-color: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.divider};
  border-radius: 6px;
  color: ${({ theme }) => theme.colors.text.secondary};
  cursor: pointer;
  display: ${({ $isVisible }) => ($isVisible ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  z-index: 150;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);
  transition: all 0.15s ease;

  &:hover {
    background-color: ${({ theme }) => theme.colors.surfaceLight};
    color: ${({ theme }) => theme.colors.text.primary};
    transform: scale(1.05);
  }

  svg {
    font-size: 20px;
  }
`;

interface LayoutProps {
  children: ReactNode;
}

const LayoutContent: React.FC<LayoutProps> = ({ children }) => {
  const { isCollapsed, toggleSidebar } = useSidebar();

  return (
    <LayoutContainer>
      <Sidebar />
      <FloatingMenuButton $isVisible={isCollapsed} onClick={toggleSidebar} title="Open sidebar">
        <FiMenu />
      </FloatingMenuButton>
      <MainContent $isCollapsed={isCollapsed}>
        <Header />
        <ContentArea>{children}</ContentArea>
      </MainContent>
    </LayoutContainer>
  );
};

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  return (
    <SidebarProvider>
      <LayoutContent>{children}</LayoutContent>
    </SidebarProvider>
  );
};
